/**
 * Listado paginado de productos, con su barra de orden.
 *
 * Lo comparten la categoria, la linea y los resultados de busqueda: es la misma
 * pantalla con distinto titulo, y duplicarla tres veces solo garantizaria que
 * las tres se separen con el tiempo.
 *
 * Los filtros por marca y precio (las facetas del rail) son su propia rebanada.
 */
import { useSearchParams } from 'react-router-dom';

import type { OrdenCatalogo, Paginado, ProductoResumen } from '@bandu/compartido';

import { numero } from '../../utilidades/formato';
import { Icono } from '../comunes/Icono';
import { GrillaProductos } from './GrillaProductos';
import { Paginador } from './Paginador';

const ORDENES: ReadonlyArray<{ valor: OrdenCatalogo; texto: string }> = [
  { valor: 'relevancia', texto: 'Mas relevantes' },
  { valor: 'precio_asc', texto: 'Precio: de menor a mayor' },
  { valor: 'precio_desc', texto: 'Precio: de mayor a menor' },
  { valor: 'nombre', texto: 'Nombre A-Z' },
];

export interface ListadoProductosProps {
  productos: Paginado<ProductoResumen>;
  /** Nombre de lo que se esta listando, para el texto accesible de la grilla. */
  titulo: string;
  /** Ruta del listado sin parametros; el paginador cuelga de ella. */
  ruta: string;
}

export function ListadoProductos({ productos, titulo, ruta }: ListadoProductosProps) {
  const [parametros, setParametros] = useSearchParams();
  const orden = parametros.get('orden') ?? 'relevancia';

  function cambiarOrden(valor: string): void {
    const siguientes = new URLSearchParams(parametros);
    siguientes.set('orden', valor);
    // Reordenar devuelve a la primera pagina: la tercera del orden anterior
    // no tiene nada que ver con la tercera del nuevo.
    siguientes.delete('pagina');
    setParametros(siguientes);
  }

  if (productos.total === 0) {
    return (
      <div className="vacio">
        <span className="vacio-icono">
          <Icono nombre="caja" />
        </span>
        <h2>Todavia no hay productos publicados aca</h2>
        <p>Estamos cargando el catalogo. Escribenos por WhatsApp y te confirmamos al tiro.</p>
      </div>
    );
  }

  return (
    <>
      <div className="barra-orden">
        <p className="conteo">
          <strong>{numero(productos.total)}</strong>{' '}
          {productos.total === 1 ? 'producto' : 'productos'}
        </p>
        <label htmlFor="orden-listado">
          Ordenar por
          <select
            id="orden-listado"
            className="selector"
            value={orden}
            onChange={(evento) => cambiarOrden(evento.target.value)}
          >
            {ORDENES.map((opcion) => (
              <option key={opcion.valor} value={opcion.valor}>
                {opcion.texto}
              </option>
            ))}
          </select>
        </label>
      </div>

      <GrillaProductos productos={productos.items} titulo={`Productos de ${titulo}`} titula />

      <Paginador
        pagina={productos.pagina}
        total={productos.total}
        porPagina={productos.porPagina}
        ruta={orden === 'relevancia' ? ruta : `${ruta}?orden=${orden}`}
      />
    </>
  );
}
